import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Mail, Smartphone, Clock, Loader2, AlertCircle, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { api } from '../../services/api';

interface NotificationPreferences {
  coachId: string;
  enabled: boolean;
  channels: {
    EMAIL: boolean;
    PUSH: boolean;
  };
  categories: {
    ASSESSMENT_DUE: { enabled: boolean };
    ASSESSMENT_OVERDUE: { enabled: boolean };
    TRAINING_REMINDER: { enabled: boolean };
  };
  trainingReminderHours: number;
}

interface PushSubscriptionRecord {
  subscriptionId: string;
  endpoint: string;
  createdAt: string;
}

const CATEGORY_LABELS: Record<string, { label: string; description: string }> = {
  ASSESSMENT_DUE: {
    label: 'Upcoming assessments',
    description: 'Reminders when player assessments are due this week',
  },
  ASSESSMENT_OVERDUE: {
    label: 'Overdue assessments',
    description: 'Alerts when player assessments are past due',
  },
  TRAINING_REMINDER: {
    label: 'Training reminders',
    description: 'Reminders before upcoming training sessions',
  },
};

const REMINDER_OPTIONS = [1, 2, 4, 6, 12, 24, 48];

/**
 * Notification settings section for the coach profile/settings page.
 * Displays toggles for global, per-channel, per-category preferences
 * and training reminder hours.
 *
 * Requirements: 15.5, 8.1, 8.2, 8.3
 */
export const NotificationSettings: React.FC = () => {
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [subscriptions, setSubscriptions] = useState<PushSubscriptionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pushSupported] = useState(
    'Notification' in window && 'serviceWorker' in navigator
  );
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const [showInstallGuide, setShowInstallGuide] = useState(false);

  // Detect platform for install guide
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || (navigator as any).standalone === true;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [prefsRes, subsRes] = await Promise.all([
        api.get('/notifications/preferences'),
        api.get('/notifications/subscriptions'),
      ]);
      setPreferences(prefsRes.data.data);
      setSubscriptions(subsRes.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const savePreferences = async (updated: NotificationPreferences) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/notifications/preferences', updated);
      setPreferences(updated);
      setSuccess('Notification preferences saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (path: string[], value: boolean) => {
    if (!preferences) return;
    const updated = JSON.parse(JSON.stringify(preferences)) as NotificationPreferences;
    let obj: any = updated;
    for (let i = 0; i < path.length - 1; i++) {
      obj = obj[path[i]];
    }
    obj[path[path.length - 1]] = value;
    savePreferences(updated);
  };

  const handleReminderHoursChange = (hours: number) => {
    if (!preferences) return;
    savePreferences({ ...preferences, trainingReminderHours: hours });
  };

  const handleEnablePush = async () => {
    try {
      const permission = await Notification.requestPermission();
      setBrowserPermission(permission);

      if (permission === 'granted') {
        const registration = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Service worker not ready after 10s')), 10000),
          ),
        ]);
        const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';
        if (!vapidKey) {
          throw new Error('VAPID public key not configured');
        }
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });

        const subJson = subscription.toJSON();
        await api.post('/notifications/subscriptions', {
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys?.p256dh || '',
            auth: subJson.keys?.auth || '',
          },
        });

        await fetchData();
        setSuccess('Push notifications enabled');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Failed to enable push:', err);
      setError('Failed to enable push notifications');
    }
  };

  const handleRemoveSubscription = async (subscriptionId: string) => {
    try {
      await api.delete(`/notifications/subscriptions/${subscriptionId}`);
      setSubscriptions((prev) => prev.filter((s) => s.subscriptionId !== subscriptionId));
      setSuccess('Subscription removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to remove subscription');
    }
  };

  if (loading) {
    return (
      <Card className="bg-white rounded-xl border border-gray-200">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-green-600 mr-3" />
          <span className="text-gray-500">Loading notification settings...</span>
        </CardContent>
      </Card>
    );
  }

  if (!preferences) {
    return (
      <Card className="bg-white rounded-xl border border-gray-200">
        <CardContent className="py-8 text-center">
          <AlertCircle className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">Unable to load notification settings.</p>
          <Button variant="outline" size="sm" onClick={fetchData} className="mt-3 min-h-[44px]">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="border-green-200 bg-green-50">
          <Bell className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Global toggle */}
      <Card className="bg-white rounded-xl border border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bell className="h-5 w-5 text-green-600" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium text-gray-900">Enable notifications</Label>
              <p className="text-sm text-gray-500">Receive reminders about assessments and training</p>
            </div>
            <Switch
              checked={preferences.enabled}
              onCheckedChange={(checked) => handleToggle(['enabled'], checked)}
              disabled={saving}
              aria-label="Toggle all notifications"
            />
          </div>
        </CardContent>
      </Card>

      {/* Channel toggles */}
      {preferences.enabled && (
        <Card className="bg-white rounded-xl border border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">Delivery channels</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-gray-500" />
                <div>
                  <Label className="text-sm font-medium text-gray-900">Email</Label>
                  <p className="text-xs text-gray-500">Receive notifications via email</p>
                </div>
              </div>
              <Switch
                checked={preferences.channels.EMAIL}
                onCheckedChange={(checked) => handleToggle(['channels', 'EMAIL'], checked)}
                disabled={saving}
                aria-label="Toggle email notifications"
              />
            </div>

            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Smartphone className="h-4 w-4 text-gray-500" />
                  <div>
                    <Label className="text-sm font-medium text-gray-900">Push notifications</Label>
                    <p className="text-xs text-gray-500">
                      {!pushSupported
                        ? isIOS && !isStandalone
                          ? 'Requires app to be added to Home Screen'
                          : 'Not supported in this browser'
                        : browserPermission === 'denied'
                        ? 'Blocked — enable in browser settings'
                        : 'Receive browser push notifications'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={preferences.channels.PUSH}
                  onCheckedChange={(checked) => handleToggle(['channels', 'PUSH'], checked)}
                  disabled={saving || !pushSupported}
                  aria-label="Toggle push notifications"
                />
              </div>

              {/* Push subscription management */}
              {preferences.channels.PUSH && pushSupported && (
                <div className="mt-3 ml-7 space-y-2">
                  {subscriptions.length > 0 ? (
                    <div className="space-y-2">
                      {subscriptions.map((sub) => (
                        <div
                          key={sub.subscriptionId}
                          className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2"
                        >
                          <span className="text-gray-600 truncate max-w-[200px]">
                            {sub.endpoint.replace(/^https?:\/\//, '').split('/')[0]}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveSubscription(sub.subscriptionId)}
                            className="text-red-500 hover:text-red-700 h-auto py-1 px-2 min-h-[44px]"
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : browserPermission === 'granted' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEnablePush}
                      className="min-h-[44px]"
                    >
                      Register this device
                    </Button>
                  ) : browserPermission !== 'denied' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEnablePush}
                      className="min-h-[44px]"
                    >
                      Enable push on this device
                    </Button>
                  ) : null}
                </div>
              )}
              {/* PWA install guide — shown when push isn't supported */}
              {!pushSupported && (isIOS || isAndroid) && (
                <div className="mt-3 ml-7">
                  {!showInstallGuide ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowInstallGuide(true)}
                      className="min-h-[44px] gap-2"
                    >
                      <Download className="h-4 w-4" />
                      How to enable push notifications
                    </Button>
                  ) : (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      <p className="text-sm font-medium text-blue-900">
                        Add GamePath to your Home Screen
                      </p>
                      <p className="text-xs text-blue-700">
                        Push notifications require the app to be installed on your device. Follow these steps:
                      </p>

                      {isIOS && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-blue-800">iPhone / iPad (Safari only):</p>
                          <ol className="text-xs text-blue-700 space-y-1.5 list-decimal list-inside">
                            <li>Open this site in <span className="font-semibold">Safari</span> (not Chrome or other browsers)</li>
                            <li>Tap the <span className="font-semibold">Share</span> button <span className="inline-block w-4 h-4 align-text-bottom">⬆️</span> at the bottom of the screen</li>
                            <li>Scroll down and tap <span className="font-semibold">"Add to Home Screen"</span></li>
                            <li>Tap <span className="font-semibold">"Add"</span> in the top right</li>
                            <li>Open GamePath from your Home Screen</li>
                            <li>Come back to this page and enable push notifications</li>
                          </ol>
                          <p className="text-xs text-blue-600 italic">
                            Note: On iOS, only Safari supports push notifications. Chrome and other browsers on iPhone cannot send push notifications.
                          </p>
                        </div>
                      )}

                      {isAndroid && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-blue-800">Android (Chrome):</p>
                          <ol className="text-xs text-blue-700 space-y-1.5 list-decimal list-inside">
                            <li>Tap the <span className="font-semibold">three-dot menu</span> (⋮) in the top right of Chrome</li>
                            <li>Tap <span className="font-semibold">"Add to Home screen"</span> or <span className="font-semibold">"Install app"</span></li>
                            <li>Tap <span className="font-semibold">"Install"</span> to confirm</li>
                            <li>Open GamePath from your Home Screen</li>
                            <li>Come back to this page and enable push notifications</li>
                          </ol>
                          <p className="text-xs text-blue-600 italic">
                            Tip: On Android, push notifications also work directly in Chrome without installing — but installing gives you the best experience.
                          </p>
                        </div>
                      )}

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowInstallGuide(false)}
                        className="text-blue-600 hover:text-blue-800 h-auto py-1 px-2 min-h-[44px]"
                      >
                        Close guide
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category toggles */}
      {preferences.enabled && (
        <Card className="bg-white rounded-xl border border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">Notification types</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(CATEGORY_LABELS).map(([key, { label, description }], idx) => (
              <div key={key}>
                {idx > 0 && <div className="border-t border-gray-100 pt-4" />}
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium text-gray-900">{label}</Label>
                    <p className="text-xs text-gray-500">{description}</p>
                  </div>
                  <Switch
                    checked={
                      preferences.categories[key as keyof typeof preferences.categories]?.enabled ??
                      true
                    }
                    onCheckedChange={(checked) =>
                      handleToggle(['categories', key, 'enabled'], checked)
                    }
                    disabled={saving}
                    aria-label={`Toggle ${label}`}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Training reminder hours */}
      {preferences.enabled &&
        preferences.categories.TRAINING_REMINDER?.enabled && (
          <Card className="bg-white rounded-xl border border-gray-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="h-4 w-4 text-green-600" />
                Training reminder timing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-3">
                How far in advance would you like to be reminded about training sessions?
              </p>
              <div className="flex flex-wrap gap-2">
                {REMINDER_OPTIONS.map((hours) => (
                  <Button
                    key={hours}
                    variant={preferences.trainingReminderHours === hours ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleReminderHoursChange(hours)}
                    disabled={saving}
                    className={`min-h-[44px] ${
                      preferences.trainingReminderHours === hours
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : ''
                    }`}
                  >
                    {hours < 24 ? `${hours}h` : `${hours / 24}d`}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  );
};

/** Convert URL-safe base64 VAPID key to Uint8Array */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
